import React, { useEffect, useState } from "react";
import api from "../api/api";
import Header from "./Header";

export default function OrdersTable() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [approvingIndex, setApprovingIndex] = useState(null);

  const user = sessionStorage.getItem("username") || "UnknownUser";


  const exportToCSV = () => {
  if (!orders.length) {
    alert("No data to export");
    return;
  }

  const headers = [
    "Customer",
    "Mobile No",
    "Order Date",
    "Total Amount",
    "Paymode",
    "Transaction No",
    "Payment Remark",
    "Payment Time",
    "Status",
  ];

  const rows = orders.map((o) => [
    o.name,
    o.mobileno,
    o.orderdate,
    o.totalAmount,
    o.paymode,
    o.transNo,
    o.paymentRemark,
    o.paymenttime,
    o.paymentStatus,
  ]);

  const csvContent = [headers, ...rows]
    .map((row) =>
      row.map((cell) => `"${String(cell ?? "").replace(/"/g, '""')}"`).join(",")
    )
    .join("\n");

  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);

  const link = document.createElement("a");
  link.href = url;
  link.download = `Payment_Approval_${new Date()
    .toISOString()
    .split("T")[0]}.csv`;

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};


  // ✅ Move fetchOrders outside useEffect so we can reuse it for auto-refresh
  const fetchOrders = async () => {
    try {
      const response = await api.GetCustomerPayment(user);
      const data = response?.GetCustomerPaymentjs?.Table || [];

      const formattedData = data.map((item) => {
        const orderDate = item.Orderdate
          ? new Date(item.Orderdate).toLocaleDateString("en-IN", {
              day: "2-digit",
              month: "2-digit",
              year: "numeric",
            })
          : "-";

        const paymentTime = item.EntryTime
          ? new Date(item.EntryTime).toLocaleString("en-IN", {
              timeZone: "Asia/Kolkata",
              day: "2-digit",
              month: "2-digit",
              year: "numeric",
              hour: "2-digit",
              minute: "2-digit",
              second: "2-digit",
              hour12: true,
            })
          : "-";

        return {
          id: item.Vid,
          OId: item.OId,
          name: item.Vname,
          mobileno: item.Vcode,
          orderdate: orderDate,
          totalAmount: item.Amount || 0,
          paymode: item.Paymode || "-",
          transNo: item.TransNo || "-",
          paymentRemark: item.PaymentRemark || "-",
          paymentStatus: "Approval",
          paymenttime: paymentTime,
          approved: false,
        };
      });

      setOrders(formattedData);
    } catch (error) {
      console.error("Error fetching orders:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();

    // 🔸 Auto-refresh every 2 minutes (120000 ms)
    const interval = setInterval(() => {
      fetchOrders();
    }, 120000);

    // 🔸 Cleanup when unmounting
    return () => clearInterval(interval);
  }, [user]);

  const handleApprovalClick = async (index) => {
    const order = orders[index];
    if (!order || order.approved) return;

    try {
      setApprovingIndex(index);
      await api.PaymentApproval(order.OId);

      setOrders((prevOrders) =>
        prevOrders.map((o, i) =>
          i === index
            ? { ...o, paymentStatus: "Approved", approved: true }
            : o
        )
      );
    } catch (error) {
      console.error("Error approving payment:", error);
      alert("Payment approval failed. Please try again.");
    } finally {
      setApprovingIndex(null);
    }
  };

  const handleBack = () => window.history.back();

  const handleLogout = () => {
    sessionStorage.removeItem("isLoggedIn");
    sessionStorage.removeItem("username");
    sessionStorage.removeItem("lastVisited");
    window.location.href = "/";
  };

  if (loading) {
    return (
      <div className="mt-16 text-center text-gray-600 animate-pulse">
        Loading payment details for <strong>{user}</strong>...
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header onLogout={handleLogout} />
      <main className="p-6 md:ml-64">
        <div className="p-4 sm:p-6 mx-auto mt-3 bg-white shadow-2xl rounded-2xl max-w-8xl">
      {/* Header Section */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b pb-3 mb-4">
        <button
          onClick={handleBack}
          className="inline-flex items-center gap-1 px-3 py-1 text-gray-800 font-semibold 
            bg-gray-200 hover:bg-gray-300 rounded-full shadow transition 
            text-sm sm:text-base w-auto self-start"
        >
          ← Back
        </button>

        <h1 className="text-2xl sm:text-3xl font-bold text-orange-600 text-center flex-1">
          Payment Approval
        </h1>
    <button
  onClick={exportToCSV}
  className="
    px-2.5 py-1.5
    text-sm font-semibold
    inline-flex items-center
    bg-blue-500 text-white
    rounded-full
    hover:bg-blue-700
    shadow
    transition
    whitespace-nowrap
  "
>
  📄 Export
</button>
        <div className="w-24"></div>
      </div>

      {/* Table Section */}
      <div className="overflow-x-auto border border-gray-200 rounded-xl shadow-inner">
        <table className="min-w-full text-sm text-gray-700">
          <thead className="sticky top-0 bg-gradient-to-r from-orange-500 to-orange-500 text-white">
            <tr>
              <th className="px-4 py-3 text-left">Customer</th>
              <th className="px-4 py-3 text-left">Mobile No</th>
              <th className="px-4 py-3 text-left">Order Date</th>
              <th className="px-4 py-3 text-right">Total Amount</th>
              <th className="px-4 py-3 text-left">Paymode</th>
              <th className="px-4 py-3 text-left">Transaction No</th>
              <th className="px-4 py-3 text-left">Payment Remark</th>
              <th className="px-4 py-3 text-left">Payment Time</th>
              <th className="px-4 py-3 text-center">Status</th>
            </tr>
          </thead>

          <tbody>
            {orders.length > 0 ? (
              orders.map((order, index) => (
                <tr
                  key={order.id}
                  className={`transition hover:bg-orange-50 ${
                    order.approved
                      ? "bg-green-100"
                      : index % 2 === 0
                      ? "bg-white"
                      : "bg-gray-50"
                  }`}
                >
                  <td className="px-4 py-3 font-semibold text-gray-900">
                    {order.name}
                  </td>
                  <td className="px-4 py-3">{order.mobileno}</td>
                  <td className="px-4 py-3">{order.orderdate}</td>
                  <td className="px-4 py-3 text-right font-medium text-gray-900">
                    ₹{parseFloat(order.totalAmount || 0).toFixed(2)}
                  </td>
                  <td className="px-4 py-3">{order.paymode}</td>
                  <td className="px-4 py-3">{order.transNo}</td>
                  <td className="px-4 py-3">{order.paymentRemark}</td>
                  <td className="px-4 py-3">{order.paymenttime}</td>
                  <td className="px-4 py-3 text-center">
                    <button
                      onClick={() => handleApprovalClick(index)}
                      disabled={order.approved || approvingIndex === index}
                      className={`px-3 py-1.5 rounded-full text-white text-sm font-medium shadow transition ${
                        order.approved
                          ? "bg-gray-400 cursor-not-allowed"
                          : approvingIndex === index
                          ? "bg-yellow-500 animate-pulse"
                          : "bg-orange-500 hover:bg-orange-600"
                      }`}
                    >
                      {approvingIndex === index
                        ? "Approving..."
                        : order.paymentStatus}
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td
                  colSpan="9"
                  className="py-6 text-center text-gray-500 font-medium"
                >
                  No payment records found for <strong>{user}</strong>.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
        </div>
      </main>
    </div>
  );
}

